<template>
    <div>
        <div class="w-full md:flex justify-end items-center">
            <div class="flex justify-between items-center space-x-2">
                <AppButton element="button" type="button" @click="showTool = !showTool">
                    {{ $t('messages.import_grades_for_the_group') }}
                </AppButton>

                <div :title="$t('messages.paste_scores_grading_tool')">
                    <IconInfoCircle class="w-6 h-6 text-gray-400" />
                </div>
            </div>
        </div>

        <div v-auto-animate>
            <AppWell v-if="showTool" class="my-2">
                <AppCard>
                    <div class="w-full">
                        <div class="px-4 py-2">
                            <label for="scoresInline">{{ $t('messages.enter_scores_separated_by_spaces') }}</label>
                            <div class="w-full">
                                <div class="mt-1">
                                    <FormInput
                                        type="text"
                                        id="scoresInline"
                                        v-model="scoresInline"
                                        placeholder="5 4 6 4 2 10 8 7 5 6 7 8 10"
                                        class="w-full"
                                    />
                                </div>
                                <div class="mt-1">
                                    <AppButton
                                        type="button"
                                        element="button"
                                        :disabled="scoresFromLine.length !== progressEvaluationGroupScores.length"
                                        @click="assignScores"
                                        :title="
                                            scoresFromLine.length === totalStudents
                                                ? ''
                                                : $t('messages.the_number_of_scores_doesnt_match_number_of_students')
                                        "
                                    >
                                        {{ $t('messages.add_scores') }}
                                    </AppButton>
                                </div>
                            </div>
                            <div>
                                {{ $t('messages.paste_scores_grading_tool_requirements') }}
                                <span>
                                    {{ scoresFromLine.length }}/
                                    {{ progressEvaluationGroupScores.length }}
                                </span>
                            </div>
                        </div>
                    </div>
                </AppCard>
            </AppWell>
        </div>

        <div class="md:grid md:grid-cols-3 md:gap-6">
            <div class="md:col-span-1">
                <h3 class="text-lg font-medium leading-6 text-gray-900">
                    {{ $t('messages.information') }}
                </h3>
                <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                    <div class="sm:col-span-2">
                        <FormLabel :value="$t('messages.date')" for="date" />
                        <FormInput id="date" v-model="date" type="date" name="date" class="mt-1" />
                    </div>

                    <div class="sm:col-span-2">
                        <label for="max_score" class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.max_score') }}
                        </label>
                        <div class="mt-1">
                            <FormInput
                                id="max_score"
                                v-model.number="maxScore"
                                type="number"
                                name="max_score"
                                class="mt-1"
                                min="1"
                                max="90"
                                required
                                step="1"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 w-40 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.score') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    d10
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.absent') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.comment') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                class="hover:bg-gray-200"
                                v-for="(score, index) in progressEvaluationGroupScores"
                                :key="score.student.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ score.student.full_name }}
                                </td>
                                <td
                                    class="px-4 sm:px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <label>
                                            <FormInput
                                                type="number"
                                                v-model.number="score.score"
                                                :name="`score[${score.student.id}]`"
                                                step="0.01"
                                                min="0"
                                                :max="maxScore"
                                                :tabindex="index + 1"
                                                :class="
                                                    parseFloat(score.score) > parseFloat(maxScore) ? 'bg-red-200' : ''
                                                "
                                            />
                                        </label>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <ScoreOnBase v-model="score.decimal_score" :score="score.score" :max="maxScore" />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <label>
                                        <input
                                            name="absent[]"
                                            disabled
                                            @click="score.score = null"
                                            :checked="score.score === null || score.score === ''"
                                            type="checkbox"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                    </label>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <label>
                                        <input
                                            type="text"
                                            v-model="score.comment"
                                            :name="`comment[${score.student.id}]`"
                                            class="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                                        />
                                    </label>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { computed, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import AppButton from '@/components/AppButton.vue';
import { IconInfoCircle } from '@tabler/icons-vue';
import ScoreOnBase from '@/components/ScoreOnBase.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';

const props = defineProps({
    dateInput: {
        type: String,
        default: '',
    },
    maxScoreInput: {
        type: [Number, String],
        default: 10,
    },
    progressEvaluationGroupScoresInput: {
        type: Array,
        required: true,
    },
});

const emit = defineEmits(['update:dateInput', 'update:maxScoreInput', 'update:progressEvaluationGroupScoresInput']);

const date = useVModel(props, 'dateInput', emit);
const maxScore = useVModel(props, 'maxScoreInput', emit);
const progressEvaluationGroupScores = useVModel(props, 'progressEvaluationGroupScoresInput', emit);

const totalStudents = computed(() => progressEvaluationGroupScores.value.length);

const showTool = ref(false);
const scoresInline = ref('');

function assignScores() {
    const scores = scoresInline.value
        .split(' ')
        .map((x) => (x === '' ? null : parseFloat(x)))
        .filter((x) => !Number.isNaN(x) || x === null);

    for (let i = 0; i < scores.length; i++) {
        let score = scores[i];
        if (progressEvaluationGroupScores.value.length > i) {
            progressEvaluationGroupScores.value[i].score = score;
        }
    }
    showTool.value = false;
}

const scoresFromLine = computed(() => {
    if (!scoresInline.value) {
        return [];
    }

    return scoresInline.value
        .split(' ')
        .map((x) => (x === '' ? null : parseFloat(x)))
        .filter((x) => !Number.isNaN(x) || x === null);
});
</script>
